import { Modal, Radio, Spin, Typography } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import TailwindButton from "components/Button";
import { UTILS } from "utils";
import FacebookSDKLoader from "./FacebookSDKLoader";

/**
 * A button component that handles Facebook/Instagram media sharing through Meta's API.
 * Manages authentication, page selection, and media publishing workflow.
 */
const MetaShareButton = ({
  text = "Save & Post",
  isPosted = false,
  disabled,
  loading,
  mediaUrl,
  mediaType, // "video" or "image"
  setLoading = () => {},
  setPauseVideo = () => {},
  onSave = () => {},
}) => {
  const { t } = useTranslation();
  const [fb_access_token, setFbAccessToken] = useState(null);
  const [isAlreadyPosted, setIsAlreadyPosted] = useState(isPosted);
  const [confirmPostAgain, setConfirmPostAgain] = useState(false);
  const [openPageSelectionModal, setOpenPageSelectionModal] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [selectedPage, setSelectedPage] = useState(null);
  const [isPublishing, setIsPublishing] = useState(false);
  const [instagramBusinessInfo, setInstagramBusinessInfo] = useState({});

  const permissions = [
    "pages_show_list",
    "business_management",
    "instagram_basic",
    "instagram_content_publish",
    "pages_read_engagement"
  ];

  /**
   * Saves the Facebook access token and opens the page selection modal.
   */
  const saveToLocalState = (token) => {
    setFbAccessToken(token);
    setOpenPageSelectionModal(true);
    setPauseVideo(true);
  };

  /**
   * Resets all component state to initial values.
   */
  const resetState = () => {
    setFbAccessToken(null);
    setOpenPageSelectionModal(false);
    setInstagramBusinessInfo({});
    setPauseVideo(false);
    setSelectedPage(null);
    setUserInfo(null);
    setLoading(false);
  };

  /**
   * Updates Instagram business account information in state.
   */
  const saveInstagramBusinessInfo = (key, value) => {
    setInstagramBusinessInfo((prev) => ({ ...prev, [key]: value }));
  };

  const handleLogout = () => {
    if(window?.FB?.logout){
      window?.FB?.logout?.();
    }
  }

  /**
   * Displays error notification and stops publishing state.
   */
  const showError = (msg) => {
    UTILS.errorNotification(msg || t("Something went wrong, please try again"));
    setIsPublishing(false);
  };

  /**
   * Initiates Facebook authentication flow or opens page selection if already authenticated.
   */
  const openFBAuth = () => {
    setLoading(true);
    let isAlreadyLoggedIn = false;

    if (fb_access_token) {
      setOpenPageSelectionModal(true);
      setPauseVideo(true);
      return;
    }

    window.FB.getLoginStatus(function (statusResponse) {
      if (statusResponse?.status === "connected") {
        // Already logged in
        saveToLocalState(statusResponse?.authResponse?.accessToken);
        isAlreadyLoggedIn = true;
      } else {
        resetState();
      }
    });
    if (!isAlreadyLoggedIn) {
      window.FB.login(
        (loginResponse) => {
          if (loginResponse?.status === "connected") {
            saveToLocalState(loginResponse?.authResponse?.accessToken);
          } else {
            resetState();
            console.log("Login cancelled or failed.");
          }
        },
        {
          scope: permissions.join(","),
        }
      );
    }
  };

  /**https://681482281681.ngrok-free.app
   * Fetches user's Facebook pages/accounts information from the API.
   */
  const getUserInfo = () => {
    setLoading(true);
    setPauseVideo(true);
    window.FB.api("/me/accounts", (response) => {
      if (!response || response.error) {
        console.error("Error fetching user info:", response);
        showError(response?.error?.message || "Unable to get user Info")
        setUserInfo(null);
      } else {
        setUserInfo(response?.data);
      }
      setLoading(false);
    });
  };

  /**
   * Closes the page selection modal and resets loading states.
   */
  const handleCloseModal = () => {
    if (isPublishing) return;
    setOpenPageSelectionModal(false);
    setLoading(false);
    setPauseVideo(false);
  };

  /**
   * Publishes the media container to Instagram and triggers save callback on success.
   */
  const pulishIgContainer = async (instaId, containerId) => {
    if (!containerId || !instaId) {
      UTILS.errorNotification(t("Please select a page first"));
      return;
    }
    const payload = {
      creation_id: containerId,
    };
    let isPublished = false;
    console.log({ payload, instaId });
    window.FB.api(
      `/${instaId}/media_publish`,
      "POST",
      payload,
      async (response) => {
        console.log("pulishIgContainer");
        if (response?.id) {
          isPublished = true;
          await onSave(null, true);
          handleLogout();
          resetState();
        } else {
          console.error("Error publishing media container:", response);
          showError(
            response?.error?.message || "Unable to publish media container"
          );
          return;
        }
      }
    );
  };

  let checkStatusTimeout = null; // Store timeout ID globally or in appropriate scope

  /**
   * Polls the media container status until ready, then triggers publishing.
   */
  const checkContainerStatus = (instaId, containerId) => {
    if (!containerId || !instaId) {
      UTILS.errorNotification(t("Please select a page first"));
      return;
    }

    const check = () => {
      window.FB.api(`/${containerId}?fields=status_code`, (response) => {
        console.log("checkContainerStatus");

        if (response?.status_code === "FINISHED") {
          clearTimeout(checkStatusTimeout); // Stop retrying
          pulishIgContainer(instaId, containerId);
        } else if (response?.status_code === "IN_PROGRESS") {
          checkStatusTimeout = setTimeout(check, 1000); // Retry after 1s
        } else {
          clearTimeout(checkStatusTimeout);
          console.log("checkContainerStatus", { response });
          showError(
            response?.error?.message || "Unable to publish media container"
          );
          return;
        }
      });
    };
    check(); // Start the first check
  };

  /**
   * Creates an Instagram media container for the uploaded media.
   */
  const createInstagramMediaContainer = (instagramBusinessInfo) => {
    if (!instagramBusinessInfo) {
      UTILS.errorNotification(t("Please select a page first"));
      return;
    }
    setIsPublishing(true);

    const payload = {
      media_type: "REELS",
      share_to_feed: true,
      // upload_type:"resumable"
    };
    if (mediaType === "video") {
      payload.video_url = mediaUrl;
    } else {
      payload.image_url = mediaUrl;
    }
    window.FB.api(
      `/${instagramBusinessInfo?.id}/media`,
      "POST",
      payload,
      (response) => {
        console.log("createInstagramMediaContainer");
        if (!response?.id) {
          console.log("createInstagramMediaContainer error", response)
          showError(
            response?.error?.message || "Unable to create media container"
          );
          return;
        }
        saveInstagramBusinessInfo("container_id", response?.id);
        checkContainerStatus(instagramBusinessInfo?.id, response?.id);
      }
    );
  };

  /**
   * Retrieves Instagram business account info for the selected page and initiates media container creation.
   */
  const getInstagramBusinessInfo = () => {
    if (!selectedPage) {
      UTILS.errorNotification(t("Please select a page first"));
      return;
    }
    setIsPublishing(true);

    if (
      instagramBusinessInfo?.container_id &&
      instagramBusinessInfo?.instagram_business_account?.id
    ) {
      checkContainerStatus(
        instagramBusinessInfo?.instagram_business_account?.id,
        instagramBusinessInfo?.container_id
      );
      return;
    }

    window.FB.api(
      `/${selectedPage?.id}?fields=instagram_business_account`,
      (response) => {
        console.log("getInstagramBusinessInfo");
        if (response?.instagram_business_account) {
          saveInstagramBusinessInfo(
            "instagram_business_account",
            response?.instagram_business_account
          );
          createInstagramMediaContainer(response?.instagram_business_account);
        } else {
          console.log({ error: response?.error })
          showError(
            response?.error?.message || "Instagram Business Account not found"
          );
        }
      }
    );
  };
  

  useEffect(() => {
    if (openPageSelectionModal && !userInfo) {
      getUserInfo();
    }
  }, [openPageSelectionModal]);

  useEffect(() => {
    setIsAlreadyPosted(isPosted);
  }, [isPosted]);

  useEffect(() => {
    handleLogout();
    return () => {
      handleLogout();
    }
  }, []);

  return (
    <>
      <FacebookSDKLoader />
      <TailwindButton
        outline
        text={text}
        disabled={loading || disabled || isPublishing}
        onClick={(e) =>
          isAlreadyPosted ? setConfirmPostAgain(true) : openFBAuth(e)
        }
      />
      {isAlreadyPosted ? (
        <Modal
          title={t("Video already posted")}
          closable={false}
          open={confirmPostAgain}
          footer={
            <div className="flex justify-center gap-4">
              <TailwindButton
                outline
                variant="danger"
                text={"Cancel"}
                onClick={() => setConfirmPostAgain(false)}
              />
              <TailwindButton
                outline
                text={"Post again"}
                onClick={() => {
                  setIsAlreadyPosted(false);
                  setConfirmPostAgain(false);
                  openFBAuth();
                }}
              />
            </div>
          }
        >
          <div className="flex flex-col gap-2 justify-center items-center">
            <Typography.Title level={5}>
              {t("Video has already been posted")}
            </Typography.Title>
            <Typography.Title
              level={4}
              type="danger"
              style={{
                margin: 0,
              }}
            >
              {t("Do you want to post it again?")}
            </Typography.Title>
          </div>
        </Modal>
      ) : (
        <Modal
          title={t("Select a Facebook Page Connected to your Instagram")}
          closable={isPublishing ? false : true}
          open={openPageSelectionModal}
          onCancel={() => handleCloseModal()}
          footer={
            <div className="flex justify-center">
              <TailwindButton
                disabled={!selectedPage || isPublishing}
                outline
                text="Post to Meta (Insta)"
                onClick={() => getInstagramBusinessInfo()}
              />
            </div>
          }
        >
          <Spin
            spinning={isPublishing}
            tip={t(
              "We're publishing your video to Instagram, Do not close this window, please wait..."
            )}
          >
            {
              userInfo
              ?
              userInfo?.length> 0 
              ?
              <div
              className="grid grid-cols-2 gap-4 overflow-y-auto"
              style={{ maxHeight: "320px", height: "320px" }}
            >
              {userInfo?.map((item) => (
                <Radio
                  key={item?.id}
                  value={item?.id}
                  checked={selectedPage?.id === item?.id}
                  onChange={() => setSelectedPage(item)}
                >
                  {item?.name}
                </Radio>
              ))}
            </div>
              : <div className="flex justify-center items-center text-red-400">
                {t("Seems, you don't have any Facebook Page connected to your Instagram or you don't manges any page directly")}
              </div>
              : null
            }
            
          </Spin>
        </Modal>
      )}
    </>
  );
};

export default MetaShareButton;
